import React, { useContext, useState } from 'react';
import { AgGridReact, AgGridColumn } from 'ag-grid-react';
import { PERMISSIONS, PermissionsContext } from 'contexts/PermissionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import {
	getFormattedPercentage,
	getFormattedSpendWithSymbol,
} from 'utils/number';
import { CategoriesCellRenderer } from '../CategoriesCellRenderer/CategoriesCellRenderer';
import ContextualMenu from 'Details/ContextualMenu';
import { Container, MenuWrapper, TableWrapper, Title } from './styles';
import { getColumns } from './helpers';
import { getCompanyTitle, getVendorsTitle } from '../utils';
import { agGridRowSelector } from '../../utils';

const TABLE_HEADER_HEIGHT = 32;
const TABLE_ROW_HEIGHT = 24;

const SpendByVendorTableView = ({
	companyData,
	currencySymbol,
	sortOrderCompany,
	showCategorisationModal,
	onViewTransactionsClick,
	showChangeStatusModal,
	showMoveToBinModal,
}) => {
	const [isMenuOpen, setMenuOpen] = useState(false);
	const [hoveredRowIndex, setHoveredRowIndex] = useState();
	const { permissions } = useContext(PermissionsContext);
	const hasWritePermissions =
		permissions && permissions.includes(PERMISSIONS.TransactionsWrite);
	const tableTitle = getCompanyTitle({
		title: companyData.company,
		sortOrderCompany,
	});
	const vendorTitle = getVendorsTitle({
		sortOrderCompany,
		title: companyData.company,
		vendorsCount: companyData.vendorsCount,
	});
	const isSelectedCompany = companyData.company === sortOrderCompany;
	const columnDefs = getColumns({
		currencySymbol,
		vendorTitle,
	});
	const spendFormatter = value =>
		Boolean(value) ? getFormattedSpendWithSymbol(value, currencySymbol) : '-';
	const rowData = companyData.spends.map(item => {
		let spend = '-';
		if (item.spend && item.spend !== 0) {
			spend = `${spendFormatter(item.spend)} (${getFormattedPercentage(
				(item.spend / companyData.totalSpend) * 100
			)})`;
		}
		return {
			...item,
			spend,
			category: item.category || '-',
		};
	});

	if (!rowData || rowData.length === 0) {
		return null;
	}

	const hideContextualTrigger = () => {
		if (!isMenuOpen) {
			setHoveredRowIndex(null);
		}
	};

	const gridOptions = {
		rowHeight: TABLE_ROW_HEIGHT,
		rowData,
	};

	return (
		<Container onMouseLeave={hideContextualTrigger}>
			<Title>{tableTitle}</Title>
			<TableWrapper isSelectedCompany={isSelectedCompany}>
				{rowData.map((row, index) => (
					<MenuWrapper
						key={row.vendorName + index}
						top={TABLE_HEADER_HEIGHT + index * TABLE_ROW_HEIGHT}
					>
						{hoveredRowIndex === index && (
							<ContextualMenu
								onViewTransactions={() => {
									onViewTransactionsClick(row, companyData);
								}}
								toggleMenu={() => {
									setMenuOpen(!isMenuOpen);
								}}
								hasWritePermissions={hasWritePermissions}
								onChangeStatus={() =>
									showChangeStatusModal({
										vendorName: row.vendorName,
										company: companyData.company,
									})
								}
								moveToBin={() => {
									showMoveToBinModal({
										vendorName: row.vendorName,
										company: companyData.company,
									});
								}}
								isOpen={hoveredRowIndex === index && isMenuOpen}
								onClose={() => setHoveredRowIndex(null)}
								onChangeCategorisation={
									showCategorisationModal
										? () => {
												showCategorisationModal({
													vendorName: row.vendorName,
													company: companyData.company,
												});
										  }
										: null
								}
							/>
						)}
					</MenuWrapper>
				))}

				<div className="ag-theme-balham">
					<AgGridReact
						{...gridOptions}
						defaultColDef={{
							suppressMenu: true,
						}}
						getRowClass={params => {
							if (!params.data.category) {
								return 'missing-data-row';
							}
							if (params.data.category) {
								return 'with-tooltip';
							}
							return '';
						}}
						tooltipShowDelay={0}
						frameworkComponents={{
							categoriesCellRenderer: CategoriesCellRenderer,
						}}
						suppressCopyRowsToClipboard
						suppressScrollOnNewData
						suppressRowDrag
						suppressRowClickSelection
						suppressCellSelection
						suppressCsvExport
						suppressExcelExport
						suppressContextMenu
						domLayout="autoHeight"
						suppressPaginationPanel
						applyColumnDefOrder
						onGridReady={params => {
							params.api.sizeColumnsToFit();
						}}
						onCellMouseOver={params => {
							const row = document.querySelectorAll(
								agGridRowSelector(params.rowIndex)
							)[0];
							if (row && params.rowIndex !== hoveredRowIndex && !isMenuOpen) {
								setHoveredRowIndex(params.rowIndex);
							}
						}}
					>
						{columnDefs.map(column => (
							<AgGridColumn {...column} key={column.field} />
						))}
					</AgGridReact>
				</div>
			</TableWrapper>
		</Container>
	);
};

export const SpendByVendorTable = props => (
	<QueryStringContext.Consumer>
		{({ currency }) => (
			<SpendByVendorTableView {...props} currency={currency} />
		)}
	</QueryStringContext.Consumer>
);

export default SpendByVendorTable;
